/**
 * Web-only stack card style that keeps every screen inside the app shell.
 *
 * @react-navigation/stack's CardContent switches a card into "page overflow"
 * mode when the navigator's layout fills document.body (phone-sized web
 * viewports): instead of `flex: 1` the card gets `minHeight: '100%'`, growing
 * to its content height so document.body can scroll the page and mobile
 * browsers can collapse their address bar.
 *
 * Our web shell (mobile/web/index.html) pins `html, body { overflow: hidden }`
 * — every scrolling surface is an inner ScrollView by design — so that page
 * mode leaves a card taller than the viewport with NOTHING scrollable: inner
 * ScrollViews inherit the grown height (scrollHeight == clientHeight), the
 * shell clips the rest, and touch/wheel both go dead. This is exactly the
 * "QR Health Passport opens but won't scroll in mobile Safari" bug — and it
 * bites any full-body stack card on a phone-sized web viewport. Desktop never
 * hits it because the widget frame keeps the navigator smaller than body.
 *
 * `cardStyle` is applied AFTER CardContent's internal page/card style, so
 * forcing `flex: 1` here restores app-shell behavior through public API. In
 * regular card mode it's a no-op (the internal style is already flex: 1).
 * Native platforms don't have the page mode at all, hence the empty object.
 */
import { Platform } from 'react-native';
import type { StackNavigationOptions } from '@react-navigation/stack';

export const APP_SHELL_CARD_STYLE: Partial<StackNavigationOptions> =
  Platform.OS === 'web' ? { cardStyle: { flex: 1 } } : {};
